import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef } from 'react';
import {
  LayoutDashboard, ShoppingCart, Store, Users, Receipt, BarChart3,
  LogOut, Shield, CreditCard, FileText, Bell,
  User as UserIcon, Settings, AlertCircle, Moon, Sun, Keyboard, Globe, Rows3,
  Camera, Package, Zap, Menu, X, Search, Activity, AlertTriangle, Download
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import SidebarFavorites from './SidebarFavorites';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { isTypingTarget } from '@/hooks/useKeyboardShortcuts';
import { LANGUAGES } from '@/lib/i18n';
import { useAuthStore, useUIStore, useBranchStore } from '@/stores';
import { useBrandingStore } from '@/stores/brandingStore';
import { useFeatureFlagsStore } from '@/stores/featureFlagsStore';
import { useWebSocket } from '@/hooks/useWebSocket';
import { QuickHelpButton } from './QuickHelpButton';
import { WhatsNewButton } from './WhatsNewDrawer';
import ModuleTour from './ModuleTour';
import { ImpersonationBanner } from './ImpersonationBanner';
import { NavGroup, fuzzyMatch, SidebarHubLink } from './NavGroup';
import { usePendingOffsets } from '@/hooks/usePendingOffsets';
import { hasPermission } from '@/config/navigationConfig';
import { CommandPalette } from './CommandPalette';
import { StockRequestPopup } from '@/components/stock/StockRequestPopup';
import { GlobalActivityFeed } from './GlobalActivityFeed';
import ForceUpdateButton from './ForceUpdateButton';
import MyBranchAccessBadge from './common/MyBranchAccessBadge';
import {
  NAV_GROUPS, MODULE_NAV_MAP, ROUTE_MODULE_MAP, MODULE_COLORS, MODULE_LABELS,
  EMPLOYEE_NAV, KEYBOARD_SHORTCUTS, DEFAULT_BOTTOM_NAV, BOTTOM_NAV_OPTIONS,
  BOTTOM_NAV_ICON_MAP, BOTTOM_NAV_PERMS,
} from '@/config/navigationConfig';

// iter530 — Format an ISO timestamp as "just now / Nm ago / Nh ago / Nd ago".
// Module-level so it doesn't get recreated on every render. The `now` param
// is supplied by the parent's 60s ticker so the text re-renders live.
function formatRelativeAge(iso, now) {
  if (!iso) return '';
  const diffMs = Math.max(0, (now ?? Date.now()) - new Date(iso).getTime());
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export const DashboardLayout = ({ children, module: moduleProp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Subscribe to the dashboard WebSocket — re-emits onto the
  // `ws:dashboard` window event-bus that our notification + stock-alert
  // hooks listen on. Mounted here (single global mount per session)
  // rather than in each page so we don't churn the socket on navigation.
  useWebSocket();
  const [unreadCount, setUnreadCount] = useState(0);
  // iter529 — Auto-fetch the running backend's git commit + start time so
  // the footer always reflects the actual deployed code, not a stale
  // REACT_APP_BUILD_VERSION env var. Cached in sessionStorage to avoid
  // hitting /api/version on every page nav.
  const [buildInfo, setBuildInfo] = useState(() => {
    try {
      const cached = sessionStorage.getItem('smarto_build_info');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  useEffect(() => {
    // iter535 — Re-fetch /api/version on window focus AND every 5 min so
    // that the footer reflects a fresh deploy without requiring the user
    // to close & re-open the tab. sessionStorage cache is bypassed when
    // the server returns a different `started_at`.
    let cancelled = false;
    const fetchVersion = async () => {
      try {
        const r = await api.get('/version');
        if (cancelled) return;
        const info = {
          commit: r.data?.commit || 'dev',
          started_at: r.data?.started_at || null,
          envTag: process.env.REACT_APP_BUILD_VERSION || '',
        };
        // Only update state + cache if the deploy actually changed,
        // otherwise we'd trigger a useless re-render every 5 min.
        if (!buildInfo || buildInfo.started_at !== info.started_at || buildInfo.commit !== info.commit) {
          setBuildInfo(info);
          try { sessionStorage.setItem('smarto_build_info', JSON.stringify(info)); } catch {}
        }
      } catch { /* fail silently */ }
    };
    if (!buildInfo) fetchVersion();
    const t = setInterval(fetchVersion, 5 * 60 * 1000); // 5 min
    const onFocus = () => fetchVersion();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [buildInfo]);
  // iter530 — small ticker so the relative deploy-age text re-renders
  // every 60 seconds without resetting. Cheap; no network calls.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const [hasHealthIncident, setHasHealthIncident] = useState(false);
  // iter845h — Live POS-failures alert badge. Polls
  // /pos/telemetry/live-count every 90s; a rose pill next to the bell
  // shows the count of unresolved checkout-fails in the last hour and
  // links straight to the failures inspector.
  const [posFailuresLastHour, setPosFailuresLastHour] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [showStockAlerts, setShowStockAlerts] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  // iter896 — Sidebar section-colour preference (persisted per-device
  // via localStorage). Toggle lives inside the Shortcuts modal.
  const [sidebarColorsOn, setSidebarColorsOn] = useState(() => {
    if (typeof window === 'undefined') return true;
    try { return localStorage.getItem('ui_sidebar_section_colors') !== '0'; }
    catch { return true; }
  });
  // iter931 — Sidebar density preference. 'comfortable' (default) keeps
  // the iter930 roomy spacing; 'compact' tightens padding + font so
  // 12"-14" laptops and tablets fit more items without scrolling. Kept
  // as a string (not boolean) so we can add a future 'ultra-compact'
  // tier without a schema break. Persisted per-device.
  //
  // iter932 — Auto-density: on the FIRST load (no saved key), sniff
  // window.innerHeight. Anything shorter than 800px → seed to
  // 'compact'. Anything taller stays 'comfortable'. This does NOT
  // override an explicit user choice — once the user picks a tier via
  // the segmented control we write to localStorage and never re-sniff.
  const [sidebarDensity, setSidebarDensity] = useState(() => {
    if (typeof window === 'undefined') return 'comfortable';
    try {
      const v = localStorage.getItem('ui_sidebar_density');
      if (v === 'compact' || v === 'comfortable') return v;
      // iter932 — Honour a previously-auto-seeded value on subsequent
      // loads so the "Auto" chip keeps showing (until the user makes
      // an explicit choice) and the picked tier persists.
      if (v === 'auto:compact') return 'compact';
      if (v === 'auto:comfortable') return 'comfortable';
      // No saved preference → auto-select by viewport height.
      const h = (typeof window.innerHeight === 'number' && window.innerHeight > 0)
        ? window.innerHeight : 900;
      const auto = h < 800 ? 'compact' : 'comfortable';
      // Seed the bucket with `auto:<tier>` so we can tell an auto-picked
      // default apart from a user-chosen one. The reader treats
      // `auto:compact` / `auto:comfortable` identically to the plain
      // values, but the segmented control shows an "Auto" hint until
      // the user actively picks a tier.
      try { localStorage.setItem('ui_sidebar_density', `auto:${auto}`); } catch { /* ignore */ }
      return auto;
    } catch { return 'comfortable'; }
  });
  // iter932 — Track whether the current density was auto-picked
  // (short viewport on first load) vs explicitly chosen. Drives the
  // "Auto" chip inside the Shortcuts modal.
  const [sidebarDensityIsAuto, setSidebarDensityIsAuto] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const v = localStorage.getItem('ui_sidebar_density') || '';
      return v.startsWith('auto:');
    } catch { return false; }
  });
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showActivityFeed, setShowActivityFeed] = useState(false);
  const [bottomNavItems, setBottomNavItems] = useState(() => {
    const saved = localStorage.getItem('ssc_bottom_nav');
    return saved ? JSON.parse(saved) : DEFAULT_BOTTOM_NAV;
  });
  const [showNavCustomize, setShowNavCustomize] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [sidebarSearchFocused, setSidebarSearchFocused] = useState(false);
  const [sidebarSelectedIdx, setSidebarSelectedIdx] = useState(0);
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_recent_searches_v1') || '[]'); }
    catch { return []; }
  });
  const sidebarSearchInputRef = useRef(null);
  const { t, lang, setLang, isRTL } = useLanguage();

  const { user, logout: storeLogout } = useAuthStore();
  const { darkMode, toggleDarkMode, compactMode, toggleCompact } = useUIStore();
  const { fetchBranches } = useBranchStore();
  const { branding, loadBranding, loaded: brandingLoaded } = useBrandingStore();
  const { loadFlags, loaded: flagsLoaded } = useFeatureFlagsStore();

  const currentUser = user || {};
  const isEmployee = currentUser.role === 'employee';
  const userPerms = currentUser.permissions || [];
  const allowedModules = currentUser.allowed_modules || [];
  const isAdmin = currentUser.role === 'admin' || currentUser.is_super_admin;

  // iter947 — tenant-wide pending cross-branch offset approvals (amber sidebar badge).
  // Only fetch for users who actually hold reports:read (the same gate the backend
  // enforces via require_permission). Unauthorized ERP users (e.g. cashiers) no
  // longer poll this protected endpoint every 60s → stops the repeated 403s.
  const canReadReports = isAdmin || hasPermission(userPerms, 'reports', currentUser.role, allowedModules);
  const { count: pendingOffsets } = usePendingOffsets(60000, canReadReports);

  const currentModule = moduleProp || ROUTE_MODULE_MAP[location.pathname];
  const isModuleRestricted = currentModule && !isAdmin && allowedModules.length > 0 && !allowedModules.includes(currentModule);

  useEffect(() => {
    if (!isEmployee) fetchBranches();
  }, [isEmployee, fetchBranches]);

  useEffect(() => {
    if (!brandingLoaded) loadBranding();
  }, [brandingLoaded, loadBranding]);

  // iter593 — Load tenant feature flags once per session so any page
  // can gate UI on `useFeatureFlagsStore().flags.<key>` without an
  // extra network call.
  useEffect(() => {
    if (!flagsLoaded) loadFlags();
  }, [flagsLoaded, loadFlags]);

  useEffect(() => {
    if (currentUser.role === 'admin' && !currentUser.is_super_admin) {
      const checkOnboarding = async () => {
        try {
          const res = await api.get('/tenants/current');
          if (res.data?.tenant && !res.data.tenant.onboarding_completed) {
            if (location.pathname !== '/onboarding') {
              navigate('/onboarding');
            }
          }
        } catch {}
      };
      checkOnboarding();
    }
  }, []); // eslint-disable-line

  // Consolidated notifications + stock-alert polling.
  // Historically this ran as three separate intervals (unread-count, center,
  // stock/alerts) which caused a polling storm in production — ~N requests/s
  // per tab × many tabs. Now:
  //   1. A single /notifications/center call every 90s provides BOTH
  //      unread_count AND the health-incident banner data (no need for
  //      the separate /notifications/unread-count poll).
  //   2. /stock/alerts every 5 min (alerts don't change minute-to-minute).
  //   3. Polls pause while the tab is hidden (document.visibilityState)
  //      and resume on focus — background tabs generate zero traffic.
  useEffect(() => {
    let notifTimer = null;
    let stockTimer = null;
    const NOTIF_MS = 90 * 1000;
    const STOCK_MS = 5 * 60 * 1000;

    const fetchNotifications = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try {
        const { data } = await api.get('/notifications/center');
        setUnreadCount(data.unread_count || 0);
        const cutoff = Date.now() - 24 * 60 * 60 * 1000;
        const recent = (data.recent_health_incidents || []).some(inc => {
          const t = Date.parse(inc.fired_at);
          return !isNaN(t) && t >= cutoff;
        });
        setHasHealthIncident(recent);
      } catch { /* silent */ }
      // iter845h — Piggyback POS failures count on the same 90s tick to
      // avoid multiplying polling load. Only relevant for admin/manager
      // roles that can access the inspector page anyway.
      if (isAdmin || currentUser?.role === 'manager') {
        try {
          const { data: pf } = await api.get('/pos/telemetry/live-count', { params: { minutes: 60 } });
          setPosFailuresLastHour(pf?.open_last_window || 0);
        } catch { /* silent — telemetry must never break the shell */ }
      }
    };

    const fetchStockAlerts = async () => {
      if (isEmployee) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try {
        const res = await api.get('/stock/alerts');
        setStockAlerts(prev => {
          // Fire the toast only on the 0 → N transition so the user isn't
          // nagged every 5 minutes with the same alert count.
          if ((res.data?.length || 0) > 0 && prev.length === 0) {
            toast.warning(`${res.data.length} item(s) below minimum stock level`, { duration: 5000 });
          }
          return res.data || [];
        });
      } catch { /* silent */ }
    };

    // Prime immediately on mount
    fetchNotifications();
    fetchStockAlerts();
    notifTimer = setInterval(fetchNotifications, NOTIF_MS);
    stockTimer = setInterval(fetchStockAlerts, STOCK_MS);

    // Re-prime on tab focus so the counts feel fresh without waiting 90s
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
        fetchStockAlerts();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Real-time push from `/api/ws/dashboard` — when the backend emits
    // `notif:new` or `stock:alert` we re-fetch immediately. Polling stays
    // as a slow fallback (90s/5m) for clients with WS disconnected or
    // browsers that drop sockets when backgrounded for hours.
    const onWsBus = (e) => {
      const ev = e?.detail;
      if (!ev || typeof ev !== 'object') return;
      if (ev.type === 'notif:new') {
        fetchNotifications();
        // Surface high-priority notifs as a toast so admins see slow-deploy
        // alerts (and other system events) without clicking the bell.
        if (ev.priority === 'high') {
          try {
            toast.warning(ev.title || 'Notification', {
              description: ev.message,
              duration: 7000,
            });
          } catch { /* ignore */ }
        }
      } else if (ev.type === 'stock:alert') {
        fetchStockAlerts();
      }
    };
    window.addEventListener('ws:dashboard', onWsBus);

    return () => {
      if (notifTimer) clearInterval(notifTimer);
      if (stockTimer) clearInterval(stockTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('ws:dashboard', onWsBus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmployee]);

  const handleKeyDown = useCallback((e) => {
    // iter929 — ALL shortcuts (incl. Ctrl+K) are ignored while typing in any
    // input / textarea / select / contenteditable. Fixes the search conflict
    // where letters typed in search boxes triggered navigation.
    if (isTypingTarget(e)) return;
    // Ctrl+K opens command palette
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      setShowCommandPalette(true);
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const target = KEYBOARD_SHORTCUTS[e.key.toLowerCase()];
    if (target === 'shortcuts') { e.preventDefault(); setShowShortcuts(s => !s); }
    else if (target) { e.preventDefault(); navigate(target); }
  }, [navigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // iter875 — Silent toast for admins when a new APK version is published.
  // Polls /api/downloads/apk/info every 5 minutes; also runs on tab focus.
  // Uses localStorage to remember the last-seen version per browser.
  useEffect(() => {
    if (!isAdmin && currentUser.role !== 'manager') return;
    let cancelled = false;
    const LS_KEY = 'smarto_apk_last_seen_version';

    const check = async () => {
      try {
        const { data } = await api.get('/downloads/apk/info');
        if (cancelled || !data?.exists || !data.version) return;
        const prev = localStorage.getItem(LS_KEY);
        if (!prev) {
          // First sighting on this browser — record silently, no toast.
          localStorage.setItem(LS_KEY, data.version);
          return;
        }
        if (prev !== data.version) {
          localStorage.setItem(LS_KEY, data.version);
          toast.info('New Smarto APK available', {
            description: `Version ${data.version.slice(0, 8)} · ${data.size_mb} MB — tap Download in POS Devices.`,
            duration: 8000,
            action: {
              label: 'Open',
              onClick: () => navigate('/settings/pos-devices'),
            },
          });
        }
      } catch { /* silent */ }
    };

    check();
    const iv = setInterval(check, 5 * 60 * 1000); // 5 min
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener('focus', onFocus);
    };
  }, [isAdmin, currentUser.role, navigate]);

  const handleLogout = () => {
    storeLogout();
    toast.success('Logged out successfully');
    navigate('/login');
    window.location.reload();
  };

  const activeModule = moduleProp || ROUTE_MODULE_MAP[location.pathname] || null;
  const allowedGroups = activeModule ? MODULE_NAV_MAP[activeModule] || [] : null;

  const { hasModuleAccess } = useAuthStore();
  const moduleFilteredGroups = NAV_GROUPS.filter(g => {
    if (isAdmin) return true;
    if (!g.module) return true;
    return hasModuleAccess(g.module);
  });

  // Sidebar always shows all groups the user has permission for, regardless
  // of the currently-active module. Previously we filtered groups to only
  // those mapped to the current page's module — that meant clicking into
  // a Sales page hid Banking, Inventory, HR, etc., forcing users to bounce
  // back to Dashboard to switch sections. Now navigation is one-click from
  // anywhere to anywhere. The accent colour and module-aware logo still
  // change based on the current module for visual context.
  const filteredNavGroups = moduleFilteredGroups;

  // iter893 — Single-accordion state. Only one section stays open at
  // a time; clicking a header collapses siblings automatically. The
  // group that owns the current path auto-wins on load and after
  // navigation, so users always see "where they are". Setting to
  // `null` collapses everything (accessible via the second click on
  // the currently-open header).
  const findGroupForPath = (path) => {
    for (const g of moduleFilteredGroups) {
      if (g.singleItem) continue; // Dashboard flat link — no accordion.
      if ((g.items || []).some(i => i.path === path)) return g.label;
    }
    return null;
  };
  const [openGroup, setOpenGroup] = useState(() => findGroupForPath(location.pathname));
  // Keep the open group in sync when the operator navigates via
  // header links, breadcrumbs, or programmatic route changes.
  useEffect(() => {
    const next = findGroupForPath(location.pathname);
    if (next && next !== openGroup) setOpenGroup(next);
  }, [location.pathname]);

  // Flat list of items the sidebar will currently render, in display order.
  // Used for ↑/↓ keyboard navigation and for capping `sidebarSelectedIdx`
  // when the search query changes.
  const flatVisibleItems = (() => {
    if (!sidebarSearch) return [];
    const out = [];
    for (const g of filteredNavGroups) {
      const groupMatches = fuzzyMatch(sidebarSearch, g.label);
      const items = g.items.filter(it => {
        if (it.superAdmin && !currentUser.is_super_admin) return false;
        if (currentUser.role === 'admin') return true;
        // Permission filtering happens inside NavGroup; we mirror the same
        // forgiving rule here (any item the user can see in the sidebar).
        return true;
      });
      for (const it of items) {
        if (groupMatches || fuzzyMatch(sidebarSearch, it.label)) {
          out.push({ path: it.path, label: it.label, group: g.label });
        }
      }
    }
    return out;
  })();

  // Reset selection when the visible list changes
  useEffect(() => {
    if (sidebarSelectedIdx >= flatVisibleItems.length) {
      setSidebarSelectedIdx(flatVisibleItems.length ? 0 : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarSearch, flatVisibleItems.length]);

  const pushRecentSearch = useCallback((q) => {
    const trimmed = (q || '').trim();
    if (!trimmed) return;
    const next = [trimmed, ...recentSearches.filter(r => r.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(next);
    try { localStorage.setItem('sidebar_recent_searches_v1', JSON.stringify(next)); } catch { /* ignore */ }
  }, [recentSearches]);

  const handleSidebarSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSidebarSearch('');
      e.currentTarget.blur();
      return;
    }
    if (!sidebarSearch || flatVisibleItems.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSidebarSelectedIdx(i => (i + 1) % flatVisibleItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSidebarSelectedIdx(i => (i - 1 + flatVisibleItems.length) % flatVisibleItems.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = flatVisibleItems[sidebarSelectedIdx] || flatVisibleItems[0];
      if (target) {
        pushRecentSearch(sidebarSearch);
        setSidebarSearch('');
        navigate(target.path);
      }
    }
  };

  const sidebarSelectedPath = flatVisibleItems[sidebarSelectedIdx]?.path || null;

  const sidebarContent = (
    <>
      <div className="px-5 py-4 border-b border-border/50 dark:border-stone-700/50">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity" data-testid="sidebar-logo-link" onClick={() => setMobileOpen(false)}>
            {activeModule ? (
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${MODULE_COLORS[activeModule] || 'from-stone-700 to-stone-900'} flex items-center justify-center shadow-sm`}>
                <span className="text-white font-bold text-sm">{(MODULE_LABELS[activeModule] || 'A')[0]}</span>
              </div>
            ) : branding.logo_url ? (
              <img
                src={/^(https?:|data:|blob:)/.test(branding.logo_url) ? branding.logo_url : `${process.env.REACT_APP_BACKEND_URL || ''}${branding.logo_url}`}
                alt={branding.app_name || 'Smarto'}
                className="w-9 h-9 rounded-xl object-contain"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/smarto-icon.png'; }}
              />
            ) : (
              <span className="text-lg font-extrabold font-outfit tracking-tight text-foreground dark:text-white shrink-0" data-testid="brand-text-fallback">Smarto&nbsp;ERP</span>
            )}
            {(activeModule || branding.logo_url || branding.app_name) && (
            <div className="flex-1 min-w-0">
              <h1 className="text-[15px] font-bold font-outfit text-foreground dark:text-white tracking-tight" data-testid="app-title">
                {activeModule ? MODULE_LABELS[activeModule] : (branding.app_name || 'Smarto')}
              </h1>
              <p className="text-[10px] text-muted-foreground truncate font-medium">{isEmployee ? 'Employee Portal' : (activeModule ? (branding.app_name || 'Smarto') : (branding.tagline || 'Enterprise Suite'))}</p>
            </div>
            )}
          </Link>
          <Link to="/notification-center" onClick={() => setMobileOpen(false)} data-testid="notification-bell"
            className="relative w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/60 dark:hover:bg-stone-700/40 transition-colors">
            <Bell size={16} className="text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-error text-white text-[9px] font-bold flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
            {hasHealthIncident && (
              <span
                className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-background animate-pulse"
                data-testid="health-incident-dot"
                title="Unresolved ERP health incident in the last 24h"
              />
            )}
          </Link>
          {/* iter845h — Live POS failures pill. Only rendered for
              admin/manager when there's at least one open failure in the
              last hour. One tap deep-links to the inspector filtered to
              open events. */}
          {(isAdmin || currentUser?.role === 'manager') && posFailuresLastHour > 0 && (
            <Link
              to="/admin/pos-failures"
              onClick={() => setMobileOpen(false)}
              data-testid="pos-failures-badge"
              title={`${posFailuresLastHour} open POS failure${posFailuresLastHour === 1 ? '' : 's'} in the last hour`}
              className="ml-1 inline-flex items-center gap-1 h-7 px-2 rounded-full bg-rose-100 hover:bg-rose-200 dark:bg-rose-950/40 dark:hover:bg-rose-900/50 text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-800 text-[10px] font-bold transition-colors animate-pulse"
            >
              <AlertTriangle size={11} />
              <span data-testid="pos-failures-badge-count">{posFailuresLastHour > 9 ? '9+' : posFailuresLastHour}</span>
              <span className="hidden sm:inline">POS</span>
            </Link>
          )}
          <WhatsNewButton />
        </div>
      </div>

      {activeModule && (
        <div className="px-3 pt-3 pb-1">
          <Link to="/apps" onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-2 rounded-[10px] text-xs font-medium text-muted-foreground hover:text-muted-foreground hover:bg-white/60 transition-colors"
            data-testid="back-to-apps">
            <LayoutDashboard size={14} /> All Applications
          </Link>
        </div>
      )}

      {/* Sticky sidebar search — fuzzy filters the 130+ nav items in
          real-time as the user types. ⌘K still opens the global Command
          Palette for full-app search (customers, invoices, etc.). */}
      <div className="px-3 pt-2 pb-2 sticky top-0 z-20 bg-[#f2f2f7]/95 dark:bg-foreground/95 backdrop-blur-md">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            ref={sidebarSearchInputRef}
            type="text"
            value={sidebarSearch}
            onChange={(e) => { setSidebarSearch(e.target.value); setSidebarSelectedIdx(0); }}
            onFocus={() => setSidebarSearchFocused(true)}
            onBlur={() => setTimeout(() => setSidebarSearchFocused(false), 150)}
            onKeyDown={handleSidebarSearchKeyDown}
            placeholder="Filter menu..."
            data-testid="sidebar-search-input"
            className="w-full pl-8 pr-16 py-2 rounded-xl bg-white/70 dark:bg-muted/60 border border-border/50 dark:border-stone-700/50 text-foreground dark:text-stone-200 placeholder:text-muted-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
          />
          {sidebarSearch ? (
            <button
              type="button"
              onClick={() => setSidebarSearch('')}
              data-testid="sidebar-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              aria-label="Clear search"
            >
              <X size={12} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowCommandPalette(true)}
              data-testid="sidebar-search-cmdk"
              title="Open command palette"
              className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:flex items-center"
            >
              <kbd className="px-1.5 py-0.5 bg-muted dark:bg-stone-700 rounded text-[10px] font-mono border border-border/50 dark:border-stone-600 text-muted-foreground">⌘K</kbd>
            </button>
          )}
        </div>
        {/* Recent searches — shown when input is focused and empty */}
        {sidebarSearchFocused && !sidebarSearch && recentSearches.length > 0 && (
          <div className="mt-2 flex items-center gap-1 flex-wrap" data-testid="sidebar-search-recents">
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 font-semibold mr-1">Recent</span>
            {recentSearches.map((r) => (
              <button
                key={r}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setSidebarSearch(r); setSidebarSelectedIdx(0); sidebarSearchInputRef.current?.focus(); }}
                data-testid={`sidebar-search-recent-${r.replace(/\s+/g, '-')}`}
                className="px-2 py-0.5 rounded-full bg-muted/80 dark:bg-stone-700/60 text-[10px] text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
              >
                {r}
              </button>
            ))}
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setRecentSearches([]);
                try { localStorage.removeItem('sidebar_recent_searches_v1'); } catch { /* ignore */ }
              }}
              data-testid="sidebar-search-recents-clear"
              className="ml-auto text-[9px] text-muted-foreground/70 hover:text-error transition-colors"
            >
              Clear
            </button>
          </div>
        )}
        {/* Keyboard hint — only when there are search results */}
        {sidebarSearch && flatVisibleItems.length > 0 && (
          <div className="mt-1.5 px-1 flex items-center gap-2 text-[9px] text-muted-foreground/70" data-testid="sidebar-search-kbd-hint">
            <span>{flatVisibleItems.length} match{flatVisibleItems.length === 1 ? '' : 'es'}</span>
            <span className="opacity-50">·</span>
            <span><kbd className="px-1 bg-muted/60 rounded text-[8px]">↑↓</kbd> navigate</span>
            <span><kbd className="px-1 bg-muted/60 rounded text-[8px]">↵</kbd> open</span>
          </div>
        )}
      </div>

      <nav className="px-1 py-2 flex-1 overflow-y-auto scrollbar-thin">
        {isEmployee ? (
          <div className="space-y-px px-2">
            {EMPLOYEE_NAV.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-2.5 px-4 py-[7px] rounded-lg text-[13px] font-medium transition-all ${isActive ? 'bg-primary/8 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'}`}>
                  <Icon size={16} strokeWidth={isActive ? 2.2 : 1.7} />
                  {item.label}
                  {item.path === '/notifications' && unreadCount > 0 && (
                    <Badge className="ml-auto bg-primary text-white text-[10px] h-5 w-5 p-0 flex items-center justify-center rounded-full">{unreadCount}</Badge>
                  )}
                </Link>
              );
            })}
          </div>
        ) : (
          <>
            <div className="px-2.5 mb-1">
              <Link to="/my-portal" onClick={() => setMobileOpen(false)}
                data-testid="nav-my-portal-top"
                className={`sidebar-nav-item flex items-center gap-2.5 px-3 py-[7px] rounded-[10px] text-[13px] transition-all ${location.pathname === '/my-portal' ? 'active bg-white dark:bg-stone-700/60 text-foreground font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.06)]' : 'text-muted-foreground hover:bg-white/60 hover:text-foreground font-medium'}`}>
                <UserIcon size={15} strokeWidth={1.6} className={location.pathname === '/my-portal' ? 'text-primary' : 'opacity-60'} />
                My Portal
              </Link>
            </div>

            <SidebarFavorites hidden={!!sidebarSearch} />

            {filteredNavGroups.map((group) => (
            (!group.singleItem && !sidebarSearch) ? (
              <SidebarHubLink
                key={group.label}
                group={group}
                currentPath={location.pathname}
                onNavigate={() => setMobileOpen(false)}
                t={t}
                colorsEnabled={sidebarColorsOn}
                density={sidebarDensity}
                pendingOffsets={pendingOffsets}
              />
            ) : (
            <NavGroup
              key={group.label}
              group={group}
              userRole={currentUser.role}
              userPerms={userPerms}
              allowedModules={allowedModules}
              currentPath={location.pathname}
              onNavigate={(path) => {
                setMobileOpen(false);
                if (sidebarSearch) {
                  pushRecentSearch(sidebarSearch);
                  setSidebarSearch('');
                }
              }}
              t={t}
              isSuperAdmin={currentUser.is_super_admin}
              searchQuery={sidebarSearch}
              searchSelectedPath={sidebarSelectedPath}
              openGroup={openGroup}
              onOpenGroupChange={setOpenGroup}
              colorsEnabled={sidebarColorsOn}
              density={sidebarDensity}
            />
            )
          ))}
          {sidebarSearch && (() => {
            // Count total matches across all permitted groups so we can
            // surface a friendly "no results" hint instead of an empty pane.
            let total = 0;
            for (const g of filteredNavGroups) {
              if (fuzzyMatch(sidebarSearch, g.label)) {
                total += g.items.length;
                continue;
              }
              for (const item of g.items) {
                if (fuzzyMatch(sidebarSearch, item.label)) total += 1;
              }
            }
            if (total > 0) return null;
            return (
              <div className="px-4 py-6 text-center" data-testid="sidebar-search-empty">
                <p className="text-xs text-muted-foreground mb-2">
                  No menu items match <span className="font-semibold text-foreground">"{sidebarSearch}"</span>
                </p>
                <button
                  type="button"
                  onClick={() => { setSidebarSearch(''); setShowCommandPalette(true); }}
                  className="text-[11px] font-medium text-primary hover:underline"
                  data-testid="sidebar-search-empty-cmdk"
                >
                  Try the global search instead →
                </button>
              </div>
            );
          })()}
          </>
        )}
      </nav>

      <div className="p-3 border-t border-border/50 dark:border-stone-700/50 bg-white/40 dark:bg-muted/40 backdrop-blur-sm">
        <div className="flex items-center gap-2.5 mb-2.5 p-2 rounded-xl bg-muted/50 dark:bg-muted/80">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center text-primary font-bold text-xs shrink-0 ring-1 ring-primary/10">
            {currentUser.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-foreground dark:text-stone-200 truncate leading-tight" data-testid="user-name">{currentUser.name}</p>
            <p className="text-[10px] text-muted-foreground truncate">{currentUser.email}</p>
          </div>
          <Badge className="bg-primary/8 text-primary border-0 capitalize text-[10px] shrink-0 font-semibold">{currentUser.role}</Badge>
        </div>
        <div className="flex gap-1.5 mb-2">
          <button onClick={toggleDarkMode} data-testid="dark-mode-toggle" className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-muted/80 dark:bg-stone-700 text-muted-foreground dark:text-muted-foreground hover:bg-muted/80 dark:hover:bg-stone-600 text-[11px] font-medium transition-colors">
            {darkMode ? <Sun size={12} /> : <Moon size={12} />}{darkMode ? t('light') : t('dark')}
          </button>
          <button
            onClick={toggleCompact}
            data-testid="compact-view-toggle"
            title="Compact View — tighter rows & smaller text across all tables"
            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${compactMode ? 'bg-primary text-primary-foreground' : 'bg-muted/80 dark:bg-stone-700 text-muted-foreground dark:text-muted-foreground hover:bg-muted/80 dark:hover:bg-stone-600'}`}
          >
            <Rows3 size={12} />{compactMode ? 'Compact' : 'Cozy'}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="language-dropdown" className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-muted/80 dark:bg-stone-700 text-muted-foreground dark:text-muted-foreground hover:bg-muted/80 dark:hover:bg-stone-600 text-[11px] font-semibold transition-colors">
                <Globe size={12} />{LANGUAGES.find(l => l.code === lang)?.flag || 'EN'}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} className={`cursor-pointer ${lang === l.code ? 'bg-primary/10 text-primary' : ''}`}>
                  <span className="font-bold mr-2">{l.flag}</span>{l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={() => setShowShortcuts(true)} data-testid="shortcuts-btn" className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-muted/80 dark:bg-stone-700 text-muted-foreground dark:text-muted-foreground hover:bg-muted/80 dark:hover:bg-stone-600 text-[11px] font-medium transition-colors">
            <Keyboard size={12} />{t('shortcuts')}
          </button>
          <button onClick={() => setShowActivityFeed(true)} data-testid="activity-feed-btn-desktop" title="Activity Feed" className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-muted/80 dark:bg-stone-700 text-muted-foreground dark:text-muted-foreground hover:bg-muted/80 dark:hover:bg-stone-600 text-[11px] font-medium transition-colors">
            <Activity size={12} />Activity
          </button>
        </div>
        {/* iter690 — "My branch access" badge. Shows the user their
            current role + branch scope so they instantly understand
            why some pages may show fewer rows. Hidden for users with
            no branch restriction (admins / unscoped). */}
        <MyBranchAccessBadge />
        <a href="https://www.smartoerp.com/api/downloads/apk/latest" download target="_blank" rel="noopener noreferrer" data-testid="sidebar-apk-download-btn"
          className="w-full flex items-center justify-center rounded-lg text-muted-foreground hover:text-emerald-600 hover:bg-emerald-500/10 text-[11px] h-8 font-medium transition-colors">
          <Download size={13} className="mr-1.5" />Download Android APK
        </a>
        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button"
          className="w-full rounded-lg text-muted-foreground hover:text-error hover:bg-error/10 text-[11px] h-8 dark:text-muted-foreground dark:hover:bg-red-900/20 font-medium">
          <LogOut size={13} className="mr-1.5" />{t('logout')}
        </Button>
      </div>
    </>
  );

  const isImpersonating = !!localStorage.getItem('impersonate_tenant_name');

  return (
    <div className={`flex min-h-screen bg-background ${darkMode ? 'dark' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {isImpersonating && <ImpersonationBanner />}
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-border px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn" className="p-1.5 rounded-lg hover:bg-muted/50">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <span className="text-base font-bold font-outfit bg-gradient-to-r from-orange-600 to-amber-500 bg-clip-text text-transparent">Smarto</span>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="language-dropdown-mobile" className="p-1 text-muted-foreground hover:text-foreground dark:text-muted-foreground text-xs font-bold flex items-center gap-1">
                <Globe size={14} />{LANGUAGES.find(l => l.code === lang)?.flag || 'EN'}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              {LANGUAGES.map((l) => (
                <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} className={`cursor-pointer ${lang === l.code ? 'bg-orange-50 text-orange-600' : ''}`}>
                  <span className="font-bold mr-2">{l.flag}</span>{l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={toggleDarkMode} data-testid="dark-mode-toggle-mobile" className="p-1 text-muted-foreground hover:text-foreground dark:text-muted-foreground">
            {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {stockAlerts.length > 0 && (
            <div className="relative">
              <button onClick={() => setShowStockAlerts(!showStockAlerts)} data-testid="stock-alerts-btn-mobile" className="relative p-1">
                <AlertCircle size={18} className="text-error" />
                <span className="absolute -top-1 -right-1 bg-error text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{stockAlerts.length}</span>
              </button>
            </div>
          )}
          {unreadCount > 0 && (
            <Link to="/notifications" className="relative">
              <Bell size={18} className="text-muted-foreground" />
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{unreadCount}</span>
            </Link>
          )}
          <button
            onClick={() => setShowActivityFeed(true)}
            data-testid="activity-feed-btn"
            className="p-1 rounded-lg hover:bg-muted/50 transition-colors"
            title="Activity Feed"
          >
            <Activity size={18} className="text-muted-foreground" />
          </button>
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-amber-400 flex items-center justify-center text-white font-bold text-[10px]">
            {currentUser.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar - desktop */}
      <aside className={`hidden lg:flex lg:flex-col w-[240px] bg-[#f2f2f7]/80 backdrop-blur-xl ${isRTL ? 'border-l' : 'border-r'} border-border/60 fixed ${isRTL ? 'right-0' : 'left-0'} h-full overflow-hidden dark:bg-foreground/95 dark:border-stone-700/50`}>
        {sidebarContent}
      </aside>

      {/* Sidebar - mobile.
          iter616 — Only mount when actually opened. Previously the
          mobile drawer was always rendered (translated off-screen)
          which (a) duplicated sidebarContent in the DOM at desktop
          widths, confusing Playwright click targets, and (b) wasted
          render cycles on heavy nav-group trees. `lg:hidden` already
          hides it visually at >=1024px, but the DOM presence still
          caused issues. */}
      {mobileOpen && (
        <aside className={`lg:hidden fixed top-0 ${isRTL ? 'right-0' : 'left-0'} z-50 w-72 h-full bg-[#f2f2f7]/95 backdrop-blur-xl ${isRTL ? 'border-l' : 'border-r'} border-border/60 flex flex-col dark:bg-foreground/95 dark:border-stone-700/50`}>
          {sidebarContent}
        </aside>
      )}

      <main className={`flex-1 min-w-0 ${isRTL ? 'lg:mr-[240px]' : 'lg:ml-[240px]'} bg-[#f5f5f7] dark:bg-foreground pb-16 lg:pb-0`}>
        <div className={`pt-14 lg:pt-0 min-w-0 ${isImpersonating ? 'mt-10' : ''}`}>
          {stockAlerts.length > 0 && (
            <div className="bg-error/10 border-b border-error/30 px-4 lg:px-8 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <AlertCircle size={16} className="text-error shrink-0" />
                  <span className="text-sm text-error font-medium truncate" data-testid="stock-alerts-banner">
                    {stockAlerts.length} {t('stock_alert_title')}
                  </span>
                </div>
                <button
                  onClick={() => setShowStockAlerts(!showStockAlerts)}
                  data-testid="stock-alerts-toggle"
                  className="text-xs text-error hover:text-error font-medium whitespace-nowrap ml-2"
                >
                  {showStockAlerts ? t('stock_alert_hide') : t('stock_alert_view')}
                </button>
              </div>
              {showStockAlerts && (
                <div className="mt-2 max-h-48 overflow-y-auto" data-testid="stock-alerts-list">
                  <div className="grid gap-1">
                    {stockAlerts.map((a) => (
                      <div key={a.item_id} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-1.5 border border-error/30" data-testid={`stock-alert-${a.item_id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-foreground truncate">{a.item_name}</span>
                          {a.category && <span className="text-muted-foreground">({a.category})</span>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-error font-semibold">{a.current_balance} {a.unit}</span>
                          <span className="text-muted-foreground">min: {a.min_level}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link to="/stock" className="block text-center text-xs text-error hover:text-error font-medium mt-2 py-1">
                    {t('go_to_inventory')} →
                  </Link>
                </div>
              )}
            </div>
          )}
          <div key={location.pathname} className="p-4 lg:p-8 page-enter min-w-0">
            {isModuleRestricted ? (
              <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="module-restricted">
                <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                  <Shield size={28} className="text-muted-foreground" />
                </div>
                <h2 className="text-xl font-outfit font-bold text-foreground mb-2">Module Restricted</h2>
                <p className="text-sm text-muted-foreground max-w-sm mb-6">You don't have access to the <strong className="text-muted-foreground">{MODULE_LABELS[currentModule] || currentModule}</strong> module. Contact your admin to request access.</p>
                <Link to="/apps" className="px-5 py-2.5 rounded-full bg-foreground text-white text-sm font-medium hover:bg-muted transition-colors">
                  Back to App Launcher
                </Link>
              </div>
            ) : children}
          </div>
          {/* Build version stamp + force-update — global footer */}
          <div className="px-4 sm:px-6 lg:px-8 py-3 mt-2 text-[10px] text-muted-foreground/70 flex items-center justify-between flex-wrap gap-1" data-testid="build-stamp-footer">
            <div className="font-mono" data-testid="build-stamp-text" title={buildInfo?.started_at ? `Backend started: ${new Date(buildInfo.started_at).toLocaleString()}` : ''}>
              {/* iter529 — auto-bumps every deploy via /api/version (git commit). Env tag kept as a fallback when offline. */}
              Smarto · build <span className="font-bold">{buildInfo?.commit || process.env.REACT_APP_BUILD_VERSION || 'dev'}</span>
              {buildInfo?.envTag && buildInfo.envTag !== buildInfo.commit && (
                <span className="opacity-60"> · {buildInfo.envTag}</span>
              )}
              {/* iter530 — relative deploy time. Re-rendered every 60s by the parent tick interval. */}
              {buildInfo?.started_at && (
                <span className="opacity-60" data-testid="build-stamp-age"> · deployed {formatRelativeAge(buildInfo.started_at, nowTick)}</span>
              )}
            </div>
            <ForceUpdateButton asMenuItem={false} />
          </div>
        </div>
      </main>

      {/* Floating Quick Entry Button */}
      {location.pathname !== '/pos' && (
        <Link to="/pos" data-testid="floating-quick-entry"
          className="fixed bottom-20 right-6 z-50 w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full shadow-lg shadow-orange-500/30 flex items-center justify-center text-white hover:scale-110 transition-transform duration-200 lg:bottom-8 lg:right-8">
          <Zap size={22} strokeWidth={2.5} />
        </Link>
      )}

      {/* Mobile Bottom Tab Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-foreground border-t border-border dark:border-stone-700 flex items-center justify-around px-1 py-1.5 safe-area-bottom" data-testid="mobile-bottom-nav">
        {(() => {
          return bottomNavItems.filter(item => {
            if (!item.enabled) return false;
            if (user?.role === 'admin') return true;
            const requiredPerm = BOTTOM_NAV_PERMS[item.path];
            if (!requiredPerm) return true;
            const perms = user?.permissions || {};
            const permLevel = typeof perms === 'object' && !Array.isArray(perms) ? perms[requiredPerm] : (Array.isArray(perms) ? perms.find(p => p.module === requiredPerm)?.level : 'none');
            return permLevel === 'read' || permLevel === 'write';
          }).slice(0, 5).map(item => {
            const Icon = BOTTOM_NAV_ICON_MAP[item.icon] || LayoutDashboard;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${isActive ? 'text-orange-600' : 'text-muted-foreground'}`}>
                <Icon size={18} strokeWidth={isActive ? 2.5 : 1.5} />
                <span className="text-[9px] font-medium">{item.label}</span>
              </Link>
            );
          });
        })()}
        <button onClick={() => setShowNavCustomize(true)} data-testid="customize-nav-btn"
          className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-muted-foreground hover:text-muted-foreground">
          <Settings size={16} strokeWidth={1.5} />
          <span className="text-[9px] font-medium">More</span>
        </button>
      </nav>

      {/* Mobile Nav Customization Modal */}
      {showNavCustomize && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50" onClick={() => setShowNavCustomize(false)}>
          <div className="bg-white dark:bg-muted rounded-t-2xl shadow-2xl w-full max-w-lg p-5 max-h-[70dvh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="nav-customize-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold font-outfit dark:text-white">Customize Bottom Navigation</h3>
              <button onClick={() => setShowNavCustomize(false)} className="text-muted-foreground hover:text-muted-foreground"><X size={18} /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Select up to 5 items to show in the bottom navigation bar.</p>
            <div className="grid grid-cols-2 gap-2">
              {BOTTOM_NAV_OPTIONS.map(item => {
                const isEnabled = bottomNavItems.find(n => n.path === item.path)?.enabled;
                const enabledCount = bottomNavItems.filter(n => n.enabled).length;
                const Icon = BOTTOM_NAV_ICON_MAP[item.icon] || LayoutDashboard;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      const existing = bottomNavItems.find(n => n.path === item.path);
                      if (existing) {
                        if (existing.enabled && enabledCount <= 1) return;
                        const updated = bottomNavItems.map(n => n.path === item.path ? { ...n, enabled: !n.enabled } : n);
                        setBottomNavItems(updated);
                        localStorage.setItem('ssc_bottom_nav', JSON.stringify(updated));
                      } else {
                        if (enabledCount >= 5) return;
                        const updated = [...bottomNavItems, { ...item, enabled: true }];
                        setBottomNavItems(updated);
                        localStorage.setItem('ssc_bottom_nav', JSON.stringify(updated));
                      }
                    }}
                    className={`flex items-center gap-2 p-3 rounded-xl text-sm font-medium transition-colors ${
                      isEnabled ? 'bg-orange-50 text-orange-600 border-2 border-orange-500' : 'bg-muted/50 text-muted-foreground border-2 border-transparent hover:bg-muted'
                    } ${enabledCount >= 5 && !isEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`nav-option-${item.label.toLowerCase()}`}
                  >
                    <Icon size={18} />
                    {item.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 rounded-full" onClick={() => {
                setBottomNavItems(DEFAULT_BOTTOM_NAV);
                localStorage.setItem('ssc_bottom_nav', JSON.stringify(DEFAULT_BOTTOM_NAV));
                toast.success('Reset to defaults');
              }}>Reset to Default</Button>
              <Button size="sm" className="flex-1 rounded-full" onClick={() => setShowNavCustomize(false)}>Done</Button>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => setShowShortcuts(false)}>
          <div className="bg-white dark:bg-muted rounded-2xl shadow-2xl max-w-md w-full mx-4 p-5 max-h-[80dvh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="shortcuts-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold font-outfit dark:text-white">Keyboard Shortcuts</h3>
              <button onClick={() => setShowShortcuts(false)} className="text-muted-foreground hover:text-muted-foreground"><X size={18} /></button>
            </div>
            <div className="space-y-3 text-sm">
              <p className="text-xs text-muted-foreground font-semibold uppercase">Quick Nav (Single Key)</p>
              {[
                ['D', 'Dashboard'], ['S', 'Sales'], ['E', 'Expenses'], ['I', 'Inventory'], ['R', 'Reports'],
                ['A', 'Analytics'], ['V', 'Visualizations'], ['T', 'Table Management'], ['L', 'Loans'],
                ['W', 'Waiter Mode'], ['C', 'Cashier POS'], ['K', 'Kitchen Display'], ['H', 'Employees'], ['O', 'Order Status'],
                ['B', 'Reconciliation'], ['F', 'Performance Report'], ['G', 'Task Compliance'], ['J', 'Invoices'],
                ['M', 'Menu Items'], ['Q', 'Schedule'],
              ].map(([key, desc]) => (
                <div key={key} className="flex items-center justify-between py-1 border-b border-border dark:border-stone-700 last:border-0">
                  <span className="text-muted-foreground dark:text-muted-foreground">{desc}</span>
                  <kbd className="bg-muted dark:bg-stone-700 text-muted-foreground dark:text-muted-foreground px-2 py-0.5 rounded text-xs font-mono font-bold">{key}</kbd>
                </div>
              ))}
              <p className="text-xs text-muted-foreground font-semibold uppercase pt-2">Advanced Shortcuts</p>
              {[
                ['Ctrl+1..9', 'Navigate to page by number'],
                ['Ctrl+N', 'New Sale'], ['Ctrl+E', 'New Expense'],
                ['Ctrl+K', 'Quick Search'], ['Ctrl+F', 'Toggle Filter'],
                ['Ctrl+Shift+E', 'Export Data'], ['Ctrl+/', 'Shortcut Help Dialog'],
                ['Ctrl+Shift+P', 'Open POS'], ['Ctrl+Shift+K', 'Kitchen Display'],
                ['Ctrl+Shift+I', 'Invoices'],
                ['Ctrl+Shift+D', 'Documents'], ['Ctrl+Shift+T', 'Transfers'],
                ['Ctrl+Shift+S', 'Settings'],
                ['Alt+P', 'Performance Report'], ['Alt+C', 'Task Compliance'],
                ['Alt+V', 'Visualizations'], ['Alt+S', 'Schedule'],
                ['Alt+L', 'Leave Approvals'], ['Alt+M', 'Menu Items'],
                ['Escape', 'Close Dialog'],
              ].map(([key, desc]) => (
                <div key={key} className="flex items-center justify-between py-1 border-b border-border dark:border-stone-700 last:border-0">
                  <span className="text-muted-foreground dark:text-muted-foreground">{desc}</span>
                  <kbd className="bg-muted dark:bg-stone-700 text-muted-foreground dark:text-muted-foreground px-2 py-0.5 rounded text-xs font-mono font-bold">{key}</kbd>
                </div>
              ))}
              {/* iter896 — Sidebar preferences. Grouped inside the
                  Shortcuts modal since that's already the "user-level
                  UI tweaks" surface — no extra Settings page needed. */}
              <p className="text-xs text-muted-foreground font-semibold uppercase pt-3">Sidebar Preferences</p>
              <div className="flex items-center justify-between py-1.5">
                <div>
                  <span className="text-muted-foreground dark:text-muted-foreground">Section colour bars</span>
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">Tinted background for each of the 6 parent sections.</p>
                </div>
                <button
                  type="button"
                  data-testid="sidebar-colors-toggle"
                  role="switch"
                  aria-checked={sidebarColorsOn}
                  onClick={() => {
                    const next = !sidebarColorsOn;
                    setSidebarColorsOn(next);
                    try { localStorage.setItem('ui_sidebar_section_colors', next ? '1' : '0'); } catch { /* ignore */ }
                  }}
                  className={`relative h-6 w-11 rounded-full transition-colors ${sidebarColorsOn ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                >
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${sidebarColorsOn ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* iter931 — Sidebar density segmented control. Compact
                   tightens vertical padding + label size by ~30% for
                   tablet + 13" laptop users who want more items above
                   the fold. Comfortable is the iter930 default.
                   iter932 — When density was auto-picked from the
                   viewport height, we surface a small "Auto" chip so
                   the user knows why Compact is pre-selected. Any
                   click flips the chip off (explicit choice wins). */}
              <div className="flex items-center justify-between py-1.5" data-testid="sidebar-density-row">
                <div>
                  <span className="text-muted-foreground dark:text-muted-foreground">Density</span>
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">Tighter row spacing for tablets · roomier for desktops.</p>
                </div>
                <div className="flex items-center gap-2">
                  {sidebarDensityIsAuto && (
                    <span
                      className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
                      title={`Auto-picked because your viewport is ${typeof window !== 'undefined' ? window.innerHeight : '?'}px tall`}
                      data-testid="sidebar-density-auto-chip"
                    >
                      Auto
                    </span>
                  )}
                  <div
                    role="radiogroup"
                    aria-label="Sidebar density"
                    className="inline-flex rounded-full bg-muted/70 dark:bg-stone-700/70 p-0.5"
                  >
                    {['compact', 'comfortable'].map((mode) => {
                      const active = sidebarDensity === mode;
                      return (
                        <button
                          key={mode}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          data-testid={`sidebar-density-${mode}`}
                          onClick={() => {
                            setSidebarDensity(mode);
                            setSidebarDensityIsAuto(false); // explicit choice
                            try { localStorage.setItem('ui_sidebar_density', mode); } catch { /* ignore */ }
                          }}
                          className={`px-3 py-1 rounded-full text-[11px] font-semibold capitalize transition-all ${
                            active
                              ? 'bg-primary text-white shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {mode}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Quick Help Button */}
      <QuickHelpButton />
      
      {/* Module-specific Guided Tours */}
      <ModuleTour />

      {/* Command Palette */}
      <CommandPalette open={showCommandPalette} onOpenChange={setShowCommandPalette} />
      <GlobalActivityFeed open={showActivityFeed} onOpenChange={setShowActivityFeed} />
      {/* iter937 — Live inter-branch stock-request popup (fulfilment loop). */}
      <StockRequestPopup />
    </div>
  );
};
